//* Vue
import type { SetupContext } from "vue";
//* 组件库
import type { UiTypes, UiModal } from "various-ui";
import { $loading, utility } from "various-ui";
//* 公共组件
import type { DefaultSearch } from "@/components/controls";
//* 组件属性
import type { ModuleData, ModuleSequence, ModuleCustomData } from "../../../index";
import type { ModuleEmits } from "../index";

export const useComposable = (emits: SetupContext<typeof ModuleEmits>["emit"]) => {
    //* 插件
    const i18n = useI18n();

    //* 静态数据
    const vars: { key?: number; data?: ModuleData; index: number; seq_types: UiTypes.candidate[] } = {
        index: 1,
        seq_types: [
            { label: "ALL", value: "All" },
            { label: "Protein", value: "Protein" },
            { label: "mRNA", value: "mRNA" },
            { label: "CDS", value: "CDS" },
        ],
    };

    //* 响应式变量
    const refs = {
        //* 输入模式
        mode: ref<"Gene" | "Custom">("Gene"),
        //* 数据源
        data: ref<ModuleData>({ id: "", mode: "Gene", gene: "", name: "", seq_type: "All", sequence: "", sequences: [], tran_pro_id: "" }),
        //* 基因检索关键词
        keyword: ref<string>(""),
        //* 序列（暂存）
        sequence: ref<string>(""),
        //* 序列类型
        seq_types: ref<UiTypes.candidate[]>([
            {
                label: i18n.t("All"),
                value: "All",
            },
        ]),
    };

    //* 节点
    const nodes = {
        modal: ref<InstanceType<typeof UiModal>>(),
        search: ref<InstanceType<typeof DefaultSearch>>(),
    };

    //* 工具函数
    const utils = {
        data: (data?: ModuleData): ModuleData => {
            data = utility.cloneDeep(data);
            if (data && (data.mode == "Gene" || data.mode == "Custom")) {
                if (data.mode == "Gene") refs.sequence.value = `>${data.name}\n${data.sequence}`;
                else if (data.mode == "Custom") {
                    refs.sequence.value = data.sequence;
                }
                return data;
            } else if (refs.mode.value == "Custom") return { mode: "Custom", name: "", sequence: "" };
            else {
                return { id: "", mode: "Gene", gene: "", name: "", seq_type: "All", sequence: "", sequences: [], tran_pro_id: "" };
            }
        },

        //* 数据解析
        resolve: (data: string) => {
            //* 字符串过滤
            const mock: { data?: ModuleData; index: number } = { index: vars.index };
            const result: ModuleData[] = [];
            data.split("\n")
                .filter((value) => value)
                .forEach((str) => {
                    const content = str.trim();
                    switch (content.charAt(0)) {
                        case ">": {
                            //* 检测是否存在待添加数据
                            mock.data && result.push(mock.data);
                            //* 数据更新
                            return (mock.data = {
                                mode: "Custom",
                                name: content.substring(1).trim() || `Query${mock.index++}`,
                                sequence: "",
                            });
                        }

                        default: {
                            if (!mock.data) {
                                return result.push({ mode: "Custom", name: `Query${mock.index++}`, sequence: content.toLocaleUpperCase() });
                            } else {
                                result.push(Object.assign(mock.data, { sequence: str.toLocaleUpperCase() }));
                                mock.data = undefined;
                                break;
                            }
                        }
                    }
                });

            return { data: result, index: mock.index };
        },

        is_custom: (data: ModuleData): data is ModuleCustomData => {
            return data.mode == "Custom";
        },
    };

    //* 函数列表
    const methods = {
        //* 序列模式（切换）
        mode: () => {
            //* 数据更新
            refs.data.value = utils.data();
            refs.keyword.value = "";
            refs.sequence.value = "";
        },

        //* 序列选择工具（激活）
        open: (data?: ModuleData, key?: number) => {
            //* 数据重置
            vars.key = key;
            refs.sequence.value = "";
            //* 数据更新
            refs.data.value = utils.data(data || vars.data);
            refs.mode.value = refs.data.value.mode;
            refs.keyword.value = refs.data.value.mode == "Gene" ? refs.data.value.gene : "";
            //* 重置完检索器状态后激活窗口
            nodes.search.value?.reset({ keyword: refs.keyword.value });
            nodes.modal.value?.openModal();
        },

        //* 基因选择
        change: (data: any) => {
            //* 序列数据获取
            $loading.open({ message: i18n.t("UnderLoad") });
            api.frontend("/api/bio/sequence-alignment/sequences", {
                data: { id: data.id },
                method: "POST",
                error: () => $loading.close(),
                success: (response) => {
                    refs.data.value.mode = "Gene";
                    if (!utils.is_custom(refs.data.value)) {
                        //* 数据更新
                        refs.data.value.id = data.id;
                        refs.data.value.gene = data.value;
                        refs.data.value.seq_type = "All";
                        refs.data.value.sequences = response || [];
                        refs.seq_types.value = vars.seq_types.filter(
                            (value) => value.value == "All" || response?.find((v: ModuleSequence) => v.type == value.value)
                        );

                        //* 默认选择第一个序列
                        if (!refs.data.value.sequences.length) $loading.close();
                        else {
                            //* 数据更新
                            refs.data.value.tran_pro_id = refs.data.value.sequences[0].value;
                            //* 触发序列选择回调
                            methods.sequence();
                        }
                    }
                },
            });
        },

        //* 序列选择
        sequence: () => {
            //* 检测数据类型是否合法
            if (utils.is_custom(refs.data.value)) $loading.close();
            else {
                //* 数据初始化
                const id = refs.data.value.tran_pro_id;
                const sequence = refs.data.value.sequences.find((value) => value.value == id);
                if (!sequence) $loading.close();
                else {
                    //* 详细序列数据获取
                    $loading.open({ message: i18n.t("UnderLoad") });
                    api.frontend("/api/bio/sequence-alignment/sequence", {
                        data: { id: refs.data.value.id, seq_type: sequence.type, tran_pro_id: sequence.id },
                        method: "POST",
                        finally: () => $loading.close(),
                        success: (response) => {
                            //* 数据更新
                            refs.data.value.name = response.name;
                            refs.data.value.sequence = response.sequence;
                            refs.sequence.value = `>${response.name}\n${response.sequence}`;
                        },
                    });
                }
            }
        },

        //* 成功事件
        success: (index?: number) => {
            //* 关闭弹窗
            nodes.modal.value?.closeModal();

            //* 数据缓存
            vars.data = refs.data.value;
            if (utils.is_custom(refs.data.value)) vars.data.sequence = refs.sequence.value;
            if (index) {
                vars.index = index;
            }
        },

        //* 确认事件
        confirm: () => {
            if (utils.is_custom(refs.data.value)) {
                emits("change", computeds.result.value.data, () => methods.success(computeds.result.value.index), vars.key);
            } else {
                emits("change", [refs.data.value], () => methods.success(), vars.key);
            }
        },
    };

    //* 计算属性
    const computeds = {
        mane: computed(() => {
            if (utils.is_custom(refs.data.value)) return false;
            else {
                const id = refs.data.value.tran_pro_id;
                if (refs.data.value.sequences.find((value) => value.value == id)?.mane) return true;
                else {
                    return false;
                }
            }
        }),

        result: computed(() => utils.resolve(refs.sequence.value)),
        sequences: computed(() => {
            if (utils.is_custom(refs.data.value)) return [];
            else {
                const type = refs.data.value.seq_type;
                if (type == "All") return refs.data.value.sequences;
                else {
                    return refs.data.value.sequences.filter((value) => value.type == type);
                }
            }
        }),
    };

    return { refs, nodes, utils, methods, computeds };
};
